import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, retry, switchMap } from 'rxjs';

import { ConfigurationService, InfraPreset, Logger, SKIP_JAP_HEADER_CONTEXT } from '@mobi/rwc-utils-common-ng-jslib';

import { TopologyIntegrationService } from '../../internal/http/topology/topology-integration.service';
import { ImpersonableUser, TestUser } from '../impersonation.types';

interface ImpersonationMappingTestuserResponse {
    upn: string;
    testusers: TestUser[];
}

interface IdpMetadata {
    response_types_supported: string[];
    jwks_uri: string;
    subject_types_supported: string[];
    id_token_signing_alg_values_supported: string[];
    registration_endpoint: string;
    token_endpoint_auth_methods_supported: string[];
    mobi_impersonations_endpoint: string;
    scopes_supported: string[];
    issuer: string;
    authorization_endpoint: string;
    userinfo_endpoint: string;
    token_endpoint: string;
}

const INFRA_PRESETS_WITH_IMPERSONATION: InfraPreset[] = ['b2e', 'b2b', 'b2eadmin'];

@Injectable({
    providedIn: 'root'
})
export class BpsImpersonationIntegrationService {
    #httpClient = inject(HttpClient);
    #configurationService = inject(ConfigurationService);
    #topologyIntegrationService = inject(TopologyIntegrationService);
    #logger = inject(Logger);

    private impersonationBaseUrl$ = this.#topologyIntegrationService.topology$.pipe(
        switchMap(topology =>
            this.#httpClient.get<IdpMetadata>(topology.business_idp_metadata_uri, SKIP_JAP_HEADER_CONTEXT)
        ),
        map(idpMetadata => idpMetadata.mobi_impersonations_endpoint)
    );

    getImpersonationMapping(userId: string | undefined): Observable<ImpersonableUser[]> {
        const emptyTestUsers = of([]);
        if (this.#configurationService.isStage('prod')) {
            this.#logger.debug('Cannot use impersonation on prod');
            return emptyTestUsers;
        }
        const infraPreset = this.#configurationService.getSync('infraPreset');
        if (!infraPreset || !INFRA_PRESETS_WITH_IMPERSONATION.includes(infraPreset)) {
            this.#logger.error(new Error(`No impersonation mapping url defined for infra preset ${infraPreset}`));
            return emptyTestUsers;
        }
        if (!userId) {
            this.#logger.error(new Error('No userId provided'));
            return emptyTestUsers;
        }
        const mappedInfraPreset = infraPreset === 'b2eadmin' ? 'b2e' : infraPreset;

        return this.impersonationBaseUrl$.pipe(
            map(baseUrl => `${baseUrl}/${mappedInfraPreset}/${userId}`),
            switchMap(url => this.#httpClient.get<ImpersonationMappingTestuserResponse>(url, SKIP_JAP_HEADER_CONTEXT)),
            map(response => response.testusers.map(u => ({ userId: u.userId, label: u.anzeigeName })) || []),
            retry({ count: 3, delay: 300 }),
            catchError(exception => {
                this.#logger.error(`Error while fetching impersonation via url from topology endpoint.`, { exception });
                return emptyTestUsers;
            })
        );
    }
}
